import {t} from '@lingui/macro'

import type {PlanResponse, SubscriptionResponse} from '~/api/types/billing'
import {PlanType} from '~/api/types/plan'
import {GridItem} from '~/generic-ui/grid'
import type Country from '~/locale/country'
import {formatCurrency} from '~/locale/currency'

import {getChooseBusinessBenefits, getChooseFreeBenefits, getChooseTeamBenefits} from './helpers'
import {
    BoxedItem,
    BoxedItemHighlighted,
    StretchGrid,
    StretchGridItem,
} from './modal-choose-plans.styles'
import PlanOption from './plan-option'

const isEligible = (planSelected: PlanType, plans?: PlanResponse[]) => {
    const planObj = plans?.find(({type}) => type === planSelected)
    return Boolean(planObj?.eligibility)
}

const LegacyPlanOptions = ({
    starterPlanUserLimit,
    subscription,
    updatePlan,
    plans,
    isOwner,
    country,
    onDismiss,
}: {
    starterPlanUserLimit: number
    subscription?: SubscriptionResponse
    updatePlan: (planType: PlanType) => void
    plans?: PlanResponse[]
    isOwner: boolean
    country?: Country
    onDismiss: Function
}) => {
    // Get tier 0 TEAM plan
    const teamPlan = plans?.find(
        ({type, interval, intervalCount, userCommitment}) =>
            type === PlanType.TEAM &&
            interval &&
            interval === 'MONTH' &&
            intervalCount === 1 &&
            !userCommitment,
    )

    // Get year BUSINESS plan
    const businessPlan = plans?.find(
        ({type, interval, intervalCount, userCommitment}) =>
            type === PlanType.BUSINESS &&
            interval &&
            interval === 'MONTH' &&
            intervalCount === 1 &&
            !userCommitment,
    )

    const teamPlanAmount = teamPlan ? formatCurrency(teamPlan.amountDecimal, teamPlan.currency) : 0
    const businessPlanAmount = businessPlan
        ? formatCurrency(businessPlan.amountDecimal, businessPlan.currency)
        : 0
    return (
        <StretchGrid margin={10}>
            <StretchGridItem auto width={322}>
                <BoxedItem>
                    <PlanOption
                        benefits={getChooseFreeBenefits()}
                        planHeader={t`Free`}
                        planType={PlanType.FREE}
                        subscription={subscription}
                        header={t`All the benefits of Free:`}
                        buttonText={t`Continue with Free`}
                        buttonOnClick={() => {
                            updatePlan(PlanType.FREE)
                        }}
                        buttonVariant="secondary"
                        isEligible={isEligible(PlanType.FREE, plans)}
                        onDismiss={onDismiss}
                        isOwner={isOwner}
                        startPlanUserLimit={starterPlanUserLimit}
                        isLegacy
                    />
                </BoxedItem>
            </StretchGridItem>
            <GridItem auto width={322}>
                <BoxedItemHighlighted>
                    <PlanOption
                        benefits={getChooseTeamBenefits(country)}
                        planHeader={t`Team`}
                        planType={PlanType.TEAM}
                        subscription={subscription}
                        header={t`All the benefits of Free, plus...`}
                        buttonText={t`Continue with Team`}
                        buttonOnClick={() => {
                            updatePlan(PlanType.TEAM)
                        }}
                        buttonVariant="primary"
                        isEligible
                        onDismiss={onDismiss}
                        isOwner={isOwner}
                        startPlanUserLimit={starterPlanUserLimit}
                        isLegacy
                        legacyPriceText={t`${teamPlanAmount} per user per month`}
                    />
                </BoxedItemHighlighted>
            </GridItem>
            <StretchGridItem auto width={322}>
                <BoxedItem>
                    <PlanOption
                        benefits={getChooseBusinessBenefits()}
                        planHeader={t`Business`}
                        planType={PlanType.BUSINESS}
                        header={t`All the benefits of Team, plus...`}
                        buttonText={t`Continue with Business`}
                        buttonOnClick={() => {
                            updatePlan(PlanType.BUSINESS)
                        }}
                        buttonVariant="secondary"
                        isEligible
                        onDismiss={onDismiss}
                        isOwner={isOwner}
                        startPlanUserLimit={starterPlanUserLimit}
                        isLegacy
                        legacyPriceText={t`${businessPlanAmount} per month`}
                    />
                </BoxedItem>
            </StretchGridItem>
        </StretchGrid>
    )
}

export default LegacyPlanOptions
